import distance from '@turf/distance';
import { useStyletron, withStyle } from 'baseui';
import { ARTWORK_TYPE, KIND, Banner } from 'baseui/banner';
import { Block } from 'baseui/block';
import { Button, SHAPE, SIZE } from 'baseui/button';
import { Alert, ArrowLeft, Delete } from 'baseui/icon';
import { StyledLink } from 'baseui/link';
import { ListHeading, ListItem, ListItemLabel } from 'baseui/list';
import { StyledNumberIcon } from 'baseui/progress-steps';
import { Skeleton } from 'baseui/skeleton';
import { Spinner } from 'baseui/spinner';
import { LatLngExpression, LatLngTuple } from 'leaflet';
import OpeningHours from 'opening_hours';
import { Fragment, h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';

import { exprToTuple } from '../utils/funcs';
import ContentCard from './ContentCard';
import { Preferences } from './PreferencesForm';
import ChangeCircle from './icons/ChangeCircle';
import Route from './icons/Route';
import Schedule from './icons/Schedule';
import Wifi, { HasWifi } from './icons/Wifi';
import {
    Cafe,
    PointsOfInterest,
    usePointsOfInterest,
    usePointsOfInterestUpdater,
} from './map/PointsOfInterestProvider';

type NominatimResponse = {
    display_name: string;
    address: {
        house_number?: string;
        road?: string;
        neighbourhood?: string;
        postcode?: string;
        suburb?: string;
    };
}
type OverpassResponse = {
    elements: Array<{
        type: 'node';
        id: number;
        lat: number;
        lon: number;
        tags: Record<string, string>;
    }>;
}

const ADDRESS = { country_code: 'us', state: 'New York' };
const ADDRESS_CACHE = {};
const CAFE_BLACKLIST_REGEX =
    /(starbucks)|(gregory'?s)|(stumptown)|(variety.*roaster)|(blank street)|(blue bottle)|(joe'?s coffee)|(pain quotidien)/i;
const CAFE_BLACKLIST_REGEX_STRING = CAFE_BLACKLIST_REGEX.toString().split('/')[1];
const INTERNET_REGEX = /(wlan)|(yes)|(wifi)/i;
// const INTERNET_REGEX_STRING = INTERNET_REGEX.toString().split('/')[1];
const OPENING_HOURS_CONFIG = {
    locale: undefined,
    map_value: true,
    mode: 0,
    tag_key: 'opening_hours',
    warnings_severity: 4,
};
const SEARCH_CACHE = {};
const WIFI_LOOKUP = {
    1: HasWifi.YES,
    0: HasWifi.UNKNOWN,
    [-1]: HasWifi.NO,
}

const NumberedListIcon = withStyle(StyledNumberIcon, () => ({
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
}));
const WarningSvgOverride = {
    Svg: {
        style: ({ $theme }) => ({
            backgroundColor: $theme.colors.warning200,
            borderRadius: '50%',
            outline: `${$theme.colors.warning200} solid`,
        }),
    },
};

export default function SearchResults({ preferences, onRestart }: { preferences: Preferences; onRestart: () => void }) {
    const [css, theme] = useStyletron();
    const [, navigate] = useLocation();
    const { cafes, highlightedCafe, user } = usePointsOfInterest();
    const setPoi = usePointsOfInterestUpdater();

    const [shouldShowWarningBanner, setShouldShowWarningBanner] = useState(false);
    const [status, setStatus] = useState<string | unknown>('pending');
    const googleMapsUrl = useMemo(() => {
        if (!user.center) return 'https://maps.google.com';
        return `https://google.com/maps/dir/${
            exprToTuple(user.center).join(',')
        }/${
            cafes.map(cafe => _shortDisplayName(cafe) || exprToTuple(cafe.position).join(',')).join('/')
        }`.replaceAll(' ', '+');
    }, [cafes, user.center]);

    useEffect(() => {
        if (status === 'pending' && preferences && user.center && user.radius) (async () => {
            try {
                const cafes: Record<number, OverpassResponse['elements'][number]> =
                    ((await _search(preferences, user))?.elements || [])
                        .reduce((acc, cur) => (acc[cur.id] = cur, acc), {});

                const today = new Date();
                const [t0, t1] = [
                    new Date(
                        (preferences.from != null && preferences.from.trim().length > 0)
                            ? today.setHours(...(preferences.from.split(':').map(Number) as [number, number]))
                            : today
                    ),
                    new Date(today.setHours(...(preferences.to.split(':').map(Number) as [number, number]))),
                ];
                const cafeOrdering = new Proxy({}, {
                    get: (target, id) => (target[id] || (target[id] = [
                            'opening_hours' in cafes[id].tags
                                ? Number(new OpeningHours(
                                    cafes[id].tags.opening_hours,
                                    {
                                        lat: cafes[id].lat,
                                        lon: cafes[id].lon,
                                        address: ADDRESS,
                                    },
                                    OPENING_HOURS_CONFIG,
                                ).getOpenDuration(t0, t1).some(t => t > 1.8e6)) * 2 - 1
                                : 0,
                            Number(Boolean(preferences.wifi)) * (
                                Number(Boolean(cafes[id].tags.internet_access?.match(INTERNET_REGEX)))
                                * 2
                                - Number('internet_access' in cafes[id].tags)
                            )
                        ]
                    ))
                });

                const stops = Number(preferences.stops);
                const selectedCafes = await Promise.all(
                    Object.values(cafes)
                        .shuffle()
                        .toSorted((a, b) => Number(cafeOrdering[b.id][0]) - Number(cafeOrdering[a.id][0]) || (cafeOrdering[b.id][1] - cafeOrdering[a.id][1]))
                        .slice(0, stops)
                        .map(async cafe => ({
                            address: await _lookUpAddress(cafe.id),
                            hasWifi: WIFI_LOOKUP[cafeOrdering[cafe.id][1]],
                            hours: cafe.tags?.opening_hours,
                            id: cafe.id,
                            name: cafe.tags?.name
                                ? `${cafe.tags.name}${cafe.tags?.branch ? ` (${cafe.tags.branch})` : ''}`
                                : '???',
                            position: [cafe.lat, cafe.lon] as LatLngTuple,
                            url: cafe.tags['website'] || cafe.tags['source:url'],
                        }))
                );

                setShouldShowWarningBanner(
                    stops > selectedCafes.length
                    || (preferences.wifi && selectedCafes.some(cafe => cafe.hasWifi === HasWifi.NO))
                );
                setPoi(poi => ({ ...poi, cafes: _bruteForceTsp(selectedCafes, poi.user.center) }));
                setStatus('fulfilled');
            } catch (e) {
                setStatus(e);
            }
        })();
    }, [status, preferences, setPoi, user]);

    if (!preferences || !user.center || !user.radius) { // change to use `status`?
        navigate('/');
        return null;
    }

    switch (status) {
    case 'pending':
        return (
            <ContentCard overrides={{
                Body: {
                    style: {
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        justifyContent: 'space-evenly',
                    },
                }
            }}>
                <Spinner $borderWidth={theme.sizing.scale100} $size={theme.sizing.scale1600} />
                <div className={css({
                    ...theme.typography.HeadingXSmall,
                    marginBottom: '15px',
                    paddingTop: theme.sizing.scale650,
                    paddingBottom: theme.sizing.scale500,
                })}>
                    Loading...
                </div>
                <Skeleton animation rows={Number(preferences.stops)} width="100%" overrides={{
                    Row: {
                        style: {
                            height: '20px',
                            marginBottom: '15px',
                        },
                    },
                }} />
            </ContentCard>
        );
    case 'fulfilled': /* eslint-disable @typescript-eslint/ban-ts-comment */
        return (
            <ContentCard overrides={{
                Body: {
                    style: {
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        justifyContent: 'space-between',
                    },
                },
            }}>
                <ListHeading endEnhancer={() => (
                    <Button $as="a"
                            endEnhancer={Route} // @ts-ignore
                            href={googleMapsUrl}
                            shape={SHAPE.pill}
                            size={SIZE.compact} // @ts-ignore
                            target="_blank">
                        See route on Google
                    </Button>
                )}
                             heading={'Your Route'}
                             maxLines={2} />
                {shouldShowWarningBanner && (
                    <Banner action={{ label: 'Close', icon: Delete, onClick: () => setShouldShowWarningBanner(false) }}
                            artwork={{ icon: Alert, type: ARTWORK_TYPE.badge }}
                            kind={KIND.warning}
                            title="We looked all over...">
                        But we couldn't find enough cafés that meet your preferences! We put as many as we could; if you
                        want to see more, try expanding your search area or changing some of your preferences!
                    </Banner>
                )}
                <ol className={css({ paddingLeft: 0, paddingRight: 0 })}>
                    {cafes.map((cafe, i) => (
                        <ListItem artwork={props => <NumberedListIcon $isActive={true} {...props}>
                            {(i + 1).toString()}
                        </NumberedListIcon>}
                                  endEnhancer={() => (
                                      <Block display="inline-flex" gridGap="8px">
                                          {!cafe.hours &&
                                              <Schedule title="hours unknown" overrides={WarningSvgOverride} />}
                                          <Wifi status={cafe.hasWifi} overrides={
                                              preferences.wifi && cafe.hasWifi === HasWifi.NO
                                                  ? WarningSvgOverride
                                                  : {}
                                          } />
                                      </Block>
                                  )}
                                  key={cafe.id}
                                  overrides={{
                                      Content: { style: { alignItems: 'flex-start' } },
                                      Root: {
                                          props: {
                                              onBlur() {
                                                  setPoi(poi => ({ ...poi, highlightedCafe: null }));
                                              },
                                              onFocus() {
                                                  setPoi(poi => ({ ...poi, highlightedCafe: i }));
                                              },
                                              tabindex: '0',
                                          },
                                          style: ({ $theme }) => ({
                                              alignItems: 'flex-start',
                                              borderColor: highlightedCafe === i
                                                  ? $theme.colors.borderAccentLight
                                                  : 'transparent',
                                              borderStyle: 'solid',
                                              borderWidth: '2px',
                                              cursor: 'pointer',
                                              paddingBottom: $theme.sizing.scale400,
                                              paddingTop: $theme.sizing.scale400,
                                              ':focus-within': { backgroundColor: $theme.colors.backgroundLightAccent },
                                              ':hover': { backgroundColor: $theme.colors.backgroundLightAccent },
                                          }),
                                      },
                                  }}>
                            <ListItemLabel description={(<>
                                <p>
                                    {(cafe.address?.neighbourhood || cafe.address?.directions)
                                        ? (<>
                                            {cafe.address.neighbourhood || ''}
                                            {cafe.address.neighbourhood && cafe.address.directions && <br />}
                                            {cafe.address.directions || ''}
                                        </>)
                                        : (cafe.address.fullDisplayName
                                            || "We're not actually sure where this is, check the map I guess")}
                                </p>
                                <p className={css({ color: theme.colors.contentTertiary })}>{cafe.hours}</p>
                            </>)}>
                                {cafe?.url
                                    ? <StyledLink animateUnderline href={cafe.url}>{cafe.name}</StyledLink>
                                    : cafe.name}
                            </ListItemLabel>
                        </ListItem>
                    ))}
                </ol>
                <Button endEnhancer={() => <ChangeCircle size={24} />}
                        onClick={() => {
                            setPoi({ cafes: [], highlightedCafe: null, user: { center: null, radius: 500 } });
                            onRestart();
                        }}
                        overrides={{
                            BaseButton: { style: ({ $theme }) => ({ marginBottom: $theme.sizing.scale600 }) },
                        }}
                        startEnhancer={() => <ArrowLeft size={24} />}>New search</Button>
            </ContentCard>
        ); /* eslint-enable @typescript-eslint/ban-ts-comment */
    default:
        console.debug(status);
        return <div>Error:<br />{JSON.stringify(status)}</div>; // TODO: render the error viewer
    }
}

/**
 * Solve the Travelling Salesman Problem with brute force using naïve great-circle distance (no isochrones).
 *
 * Brute force is `Ο(n!)` which is less than Held-Karp (`Ο(n²2ⁿ)`) for `n < 7`, and there will never be more than 6
 * "cities", since there can only be 5 cafés (plus the start location itself).
 * @param {Cafe[]} cafes cafés the user would like to visit
 * @param {LatLngExpression} start position to start from (and return to)
 * @return {Cafe[]} the ordered list of cafés with the shortest great-circle path distance.
 */
function _bruteForceTsp(cafes: Cafe[], start: LatLngExpression): Cafe[] {
    const seen = {};
    const totalDistance = (path: Cafe[]) =>
        [{ id: 'start', position: start }, ...path, { id: 'start', position: start }]
            .windows(2)
            .reduce((acc, [a, b]) =>
                acc + (
                    seen[a.id]?.[b.id]
                    || seen[b.id]?.[a.id]
                    || ((seen[a.id] ||= {})[b.id] = distance(exprToTuple(a.position), exprToTuple(b.position)))
                ), 0);
    return cafes.permutations().reduce((best, cur) => totalDistance(cur) < totalDistance(best) ? cur : best);
}

// TODO: add mocks for these next two functions to save on API calls
async function _lookUpAddress(osmId: number): Promise<Cafe['address'] | undefined> {
    return ADDRESS_CACHE[osmId] || fetch(`https://nominatim.openstreetmap.org/lookup?${new URLSearchParams({
        osm_ids: `N${osmId}`,
        format: 'jsonv2',
        addressdetails: '1',
    }).toString()}`)
        .then<NominatimResponse[], undefined>(resp => resp.json(), () => undefined)
        .then(resp => {
            const addr = resp?.at(0);
            return ADDRESS_CACHE[osmId] = addr
                ? {
                    neighbourhood: ['neighbourhood', 'suburb']
                        .map(str => addr.address[str])
                        .filter(str => str && !str.match(/community/i))
                        .join(' | ')
                        .trim(),
                    directions: ['house_number', 'road']
                        .map(str => addr.address[str])
                        .filter(Boolean)
                        .join(' ')
                        .trim(),
                    fullDisplayName: addr.display_name,
                    zipCode: addr.address?.postcode,
                }
                : undefined;
        });
}

async function _search(preferences: Preferences, user: PointsOfInterest['user']): Promise<OverpassResponse> {
    const userCenterTuple = exprToTuple(user.center);
    const key = [user.radius, ...userCenterTuple, preferences?.wifi].join(',');
    if (key in SEARCH_CACHE) return SEARCH_CACHE[key];

    return fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: `data=${encodeURIComponent(`\
[out:json][timeout:10];
rel(175905);
map_to_area -> .nyc;
node(area.nyc)(around:${user.radius},${userCenterTuple.join(',')})(newer:"2021-01-01T00:00:00Z")[amenity=cafe]${
    !preferences?.chains // preferences that do not have an ambiguous case can be handled directly in the query
        ? `["name"!~"${CAFE_BLACKLIST_REGEX_STRING}",i]` 
        : ''
};
out body geom;`.trim())}`,
    }).then(resp => SEARCH_CACHE[key] = resp.json());
}

function _shortDisplayName(cafe: Cafe) {
    if (!cafe.address.directions && !cafe.address.neighbourhood) return '';
    return `${
        cafe.name
    }, ${
        cafe.address.directions || ''
    }${
        cafe.address.directions ? ', ' : ''
    }${
        cafe.address.neighbourhood?.replace(' | ', ', ') || ''
    }${
        cafe.address.neighbourhood ? ', ' : ''
    }New York, NY ${
        cafe.address.zipCode || ''
    }`.trim();
}
